import React, { forwardRef, Fragment, MouseEvent, useState } from "react";

import { bemClassNames } from "@multiweb/reactUtils";
import { IconCode } from "@multiweb/types/contracts";
import { Cursor } from "@multiweb/types/multiweb";
import { ForwardRefType, MouseButton } from "@multiweb/types/react";
import { ButtonBackgroundColor, ButtonThemeVariant } from "../ButtonProvider";
import { EditComponent, EditComponentProps } from "../EditComponent";
import { IconColor } from "../Icon/IconEnums";
import { IconButton } from "../IconButton";
import { InputType } from "../Input";
import { SpacingSize } from "../Layout";
import { FormComponentEventHandlersProps, FormComponentProps } from "../types";
import { generateFormComponentClasses } from "../utils";

const classNames = bemClassNames("password-edit-component");

export type PasswordEditComponentProps = {
    /** Příznak, zda-li se má zobrazit tlačítko pro odkrytí hesla */
    showUnhideButton?: boolean;
    /** Hodnota kurzoru předávaná níže do tlačítka */
    cursor?: Cursor;
} & EditComponentProps
& FormComponentProps
& FormComponentEventHandlersProps;

export const PasswordEditComponent: ForwardRefType<HTMLInputElement, PasswordEditComponentProps> = forwardRef<HTMLInputElement, PasswordEditComponentProps>(({
    id,
    onBlur,
    onChange,
    onFocus,
    onMouseEnter,
    onMouseLeave,
    onMouseOver,
    onClick,
    encrypted = false,
    required = false,
    showUnhideButton = true,
    disabled = false,
    invalid = false,
    backgroundColor,
    readOnly = true,
    onJavaScriptChange,
    value,
    cursor,
}, ref) => {
    const classes = generateFormComponentClasses(classNames, {
        disabled,
        encrypted,
        invalid,
        required,
        backgroundColor,
        readOnly,
    }, [classNames()]);

    const [maskCharacters, setMaskCharacters] = useState<boolean>(true);
    const onIconClick = (e: MouseEvent<HTMLButtonElement>) => {
        onClick && onClick(e);
    };

    const onMouseOut = (_e: MouseEvent) => {
        if (!maskCharacters) {
            setMaskCharacters(true);
        }
    };
    const onMouseUp = (e: MouseEvent) => {
        if (e.button === MouseButton.leftButton) {
            setMaskCharacters(true);
        }
    };
    const onMouseDown = (e: MouseEvent) => {
        if (e.button === MouseButton.leftButton) {
            setMaskCharacters(false);
        }
    };

    const editComponentType = maskCharacters ? InputType.password : InputType.text;

    const edit = <EditComponent {...{
        id,
        encrypted,
        invalid,
        onClick,
        onMouseLeave,
        onMouseEnter,
        onMouseOver,
        onFocus,
        ref,
        backgroundColor,
        readOnly,
        type: editComponentType,
        transparent: true,
        value,
        onChange,
        onBlur,
        onJavaScriptChange,
    }}
    />;

    return showUnhideButton ? (
        <div className={classes}>
            <div className={classNames("password")}>
                {edit}
            </div>
            <IconButton iconCode={IconCode.uiEye} onClick={onIconClick} {...{ onMouseOut, onMouseUp, onMouseDown, disabled, invalid, cursor }} spacingSize={SpacingSize.none}
                variant={ButtonThemeVariant.valuePicker} backgroundColor={ButtonBackgroundColor.inherit} color={IconColor.inherit} />
        </div>
    ) : (
        <Fragment>
            {edit}
        </Fragment>
    );
});
